/**
 * 🔊 Serviço de Som Robusto para Notificações
 * 
 * Este serviço resolve problemas comuns de autoplay no browser:
 * 1. Usa HTML Audio como prioridade (melhor compatibilidade)
 * 2. Usa Web Audio API como fallback
 * 3. Pré-carrega áudio para menor latência
 * 4. Desbloqueia áudio com interação do usuário
 * 5. Suporta múltiplos formatos de áudio
 * 6. Suporta som diferente para grupos
 * 7. ⭐ Suporta som especial para tickets favoritos (starred)
 */

// Som de tickets individuais - arquivo público
const INDIVIDUAL_SOUND_URL = "/sounds/beep_individual.ogg";

// Som de grupos - arquivo público
const GROUP_SOUND_URL = "/sounds/notification_group.ogg";

// ⭐ Som especial para tickets favoritos - para contatos importantes (ex: chefe)
const STARRED_SOUND_URL = "/sounds/starred_song.ogg";

class SoundService {
  constructor() {
    this.audioContext = null;
    this.audioBuffer = null;
    this.groupAudioBuffer = null; // Buffer para som de grupos
    this.starredAudioBuffer = null; // ⭐ Buffer para som de favoritos
    this.htmlAudio = null;
    this.htmlAudioGroup = null; // Audio element para grupos
    this.htmlAudioStarred = null; // ⭐ Audio element para favoritos
    this.isUnlocked = false;
    this.volume = 1;
    this.lastPlayTime = 0;
    this.minInterval = 500; // Intervalo mínimo entre sons (ms)
    
    // Inicializar ao carregar
    this.init();
  }

  /**
   * Inicializa o serviço de áudio
   */
  async init() {
    try {
      // Tentar criar Web Audio API Context
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        this.audioContext = new AudioContext();
        await this.loadAudioBuffer();
        await this.loadGroupAudioBuffer(); // Carregar som de grupos
        await this.loadStarredAudioBuffer(); // ⭐ Carregar som de favoritos
      }
      
      // Criar elemento HTML Audio como fallback (som padrão - tickets individuais)
      this.htmlAudio = new Audio(INDIVIDUAL_SOUND_URL);
      this.htmlAudio.preload = "auto";
      this.htmlAudio.load();
      
      // Criar elemento HTML Audio para grupos
      this.htmlAudioGroup = new Audio(GROUP_SOUND_URL);
      this.htmlAudioGroup.preload = "auto";
      this.htmlAudioGroup.load();
      
      // ⭐ Criar elemento HTML Audio para favoritos
      this.htmlAudioStarred = new Audio(STARRED_SOUND_URL);
      this.htmlAudioStarred.preload = "auto";
      this.htmlAudioStarred.load();
      
      // Configurar desbloqueio de áudio em interações do usuário
      this.setupUnlock();
      
      console.log("🔊 [SoundService] Inicializado com sucesso (inclui som de grupos e favoritos)");
    } catch (err) {
      console.warn("⚠️ [SoundService] Erro ao inicializar:", err);
    }
  }

  /**
   * Carrega o buffer de áudio padrão para Web Audio API
   */
  async loadAudioBuffer() {
    try {
      const response = await fetch(INDIVIDUAL_SOUND_URL);
      const arrayBuffer = await response.arrayBuffer();
      this.audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      console.log("🔊 [SoundService] Buffer de áudio padrão carregado");
    } catch (err) {
      console.warn("⚠️ [SoundService] Erro ao carregar buffer padrão:", err);
    }
  }

  /**
   * Carrega o buffer de áudio de grupos para Web Audio API
   */
  async loadGroupAudioBuffer() {
    try {
      const response = await fetch(GROUP_SOUND_URL);
      const arrayBuffer = await response.arrayBuffer();
      this.groupAudioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      console.log("👥 [SoundService] Buffer de áudio de grupos carregado");
    } catch (err) {
      console.warn("⚠️ [SoundService] Erro ao carregar buffer de grupos:", err);
    }
  }

  /**
   * ⭐ Carrega o buffer de áudio de favoritos para Web Audio API
   */
  async loadStarredAudioBuffer() {
    try {
      const response = await fetch(STARRED_SOUND_URL);
      const arrayBuffer = await response.arrayBuffer();
      this.starredAudioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      console.log("⭐ [SoundService] Buffer de áudio de favoritos carregado");
    } catch (err) {
      console.warn("⚠️ [SoundService] Erro ao carregar buffer de favoritos:", err);
    }
  }

  /**
   * Configura desbloqueio de áudio em interações do usuário
   * Browsers modernos bloqueiam autoplay até interação do usuário
   */
  setupUnlock() {
    const unlockAudio = async () => {
      if (this.isUnlocked) return;
      
      try {
        // Desbloquear Web Audio API
        if (this.audioContext && this.audioContext.state === "suspended") {
          await this.audioContext.resume();
        }
        
        // Desbloquear HTML Audio padrão
        if (this.htmlAudio) {
          this.htmlAudio.muted = true;
          await this.htmlAudio.play().catch(() => {});
          this.htmlAudio.pause();
          this.htmlAudio.muted = false;
          this.htmlAudio.currentTime = 0;
        }
        
        // Desbloquear HTML Audio de grupos
        if (this.htmlAudioGroup) {
          this.htmlAudioGroup.muted = true;
          await this.htmlAudioGroup.play().catch(() => {});
          this.htmlAudioGroup.pause();
          this.htmlAudioGroup.muted = false;
          this.htmlAudioGroup.currentTime = 0;
        }
        
        // ⭐ Desbloquear HTML Audio de favoritos
        if (this.htmlAudioStarred) {
          this.htmlAudioStarred.muted = true;
          await this.htmlAudioStarred.play().catch(() => {});
          this.htmlAudioStarred.pause();
          this.htmlAudioStarred.muted = false;
          this.htmlAudioStarred.currentTime = 0;
        }
        
        this.isUnlocked = true;
        console.log("🔓 [SoundService] Áudio desbloqueado");
        
        // Remover listeners após desbloquear
        document.removeEventListener("click", unlockAudio);
        document.removeEventListener("touchstart", unlockAudio);
        document.removeEventListener("keydown", unlockAudio);
      } catch (err) {
        console.warn("⚠️ [SoundService] Erro ao desbloquear áudio:", err);
      }
    };

    // Adicionar listeners para interações do usuário
    document.addEventListener("click", unlockAudio);
    document.addEventListener("touchstart", unlockAudio);
    document.addEventListener("keydown", unlockAudio);
  }

  /**
   * Define o volume (0 a 1)
   */
  setVolume(vol) {
    this.volume = Math.max(0, Math.min(1, vol));
    if (this.htmlAudio) {
      this.htmlAudio.volume = this.volume;
    }
    if (this.htmlAudioGroup) {
      this.htmlAudioGroup.volume = this.volume;
    }
    if (this.htmlAudioStarred) {
      this.htmlAudioStarred.volume = this.volume;
    }
  }

  /**
   * Toca o som de notificação padrão
   * Usa múltiplas estratégias para garantir que o som seja tocado
   */
  async play() {
    return this._playSound("individual");
  }

  /**
   * Toca o som de notificação de grupos
   * Som diferenciado para mensagens de grupos
   */
  async playGroup() {
    return this._playSound("group");
  }

  /**
   * ⭐ Toca o som de notificação de favoritos
   * Som especial para tickets marcados como favoritos (contatos importantes)
   */
  async playStarred() {
    return this._playSound("starred");
  }

  /**
   * Método interno para tocar som
   * @param {string} soundType - Tipo de som: "individual", "group" ou "starred"
   */
  async _playSound(soundType = "individual") {
    if (this.volume === 0) {
      return false;
    }

    const now = Date.now();
    const timeSinceLast = now - this.lastPlayTime;

    // "starred" nunca é ignorado por debounce (prioridade máxima: chefe ligando)
    if (soundType !== "starred" && timeSinceLast < this.minInterval) {
      this._queuedCount = (this._queuedCount || 0) + 1;
      return false;
    }

    this.lastPlayTime = now;
    this._queuedCount = 0;

    let played = false;
    
    // ⭐ Mapear tipo de som para recursos
    const soundConfig = {
      individual: {
        htmlAudio: this.htmlAudio,
        url: INDIVIDUAL_SOUND_URL,
        buffer: this.audioBuffer,
        label: "padrão",
      },
      group: {
        htmlAudio: this.htmlAudioGroup,
        url: GROUP_SOUND_URL,
        buffer: this.groupAudioBuffer,
        label: "grupo",
      },
      starred: {
        htmlAudio: this.htmlAudioStarred,
        url: STARRED_SOUND_URL,
        buffer: this.starredAudioBuffer,
        label: "⭐ favorito",
      },
    };
    
    const config = soundConfig[soundType] || soundConfig.individual;

    // 🔊 PRIORIDADE: HTML Audio Element primeiro (funciona melhor antes do user gesture)
    // Isso evita o warning "AudioContext was not allowed to start"
    if (!played && config.htmlAudio) {
      played = await this.playWithHtmlAudio(config.htmlAudio);
    }

    // Estratégia 2: Criar novo elemento Audio (fallback rápido)
    if (!played) {
      played = await this.playWithNewAudio(config.url);
    }

    // Estratégia 3: Web Audio API (último recurso - requer user gesture)
    if (!played && this.audioContext && config.buffer) {
      // Tentar resumir o contexto antes
      if (this.audioContext.state === "suspended") {
        try {
          await this.audioContext.resume();
        } catch (e) {
          // Ignorar erro de resume
        }
      }
      if (this.audioContext.state === "running") {
        played = await this.playWithWebAudio(config.buffer);
      }
    }

    if (played) {
      console.log(`🔊 [SoundService] Som ${config.label} tocado com sucesso`);
    } else {
      console.warn(`⚠️ [SoundService] Não foi possível tocar o som ${config.label}`);
    }

    return played;
  }

  /**
   * Toca usando Web Audio API
   * @param {AudioBuffer} buffer - Buffer de áudio a ser tocado
   */
  async playWithWebAudio(buffer) {
    try {
      // Não tentar se o contexto estiver suspenso (evita warning)
      if (this.audioContext.state === "suspended") {
        return false;
      }

      const source = this.audioContext.createBufferSource();
      const gainNode = this.audioContext.createGain();
      
      source.buffer = buffer;
      gainNode.gain.value = this.volume;
      
      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      source.start(0);
      return true;
    } catch (err) {
      // Silenciar erro de Web Audio - HTML Audio é o fallback principal
      return false;
    }
  }

  /**
   * Toca usando HTML Audio Element pré-carregado
   * @param {HTMLAudioElement} audioElement - Elemento de áudio a ser tocado
   */
  async playWithHtmlAudio(audioElement) {
    try {
      audioElement.currentTime = 0;
      audioElement.volume = this.volume;
      
      await audioElement.play();
      return true;
    } catch (err) {
      console.warn("⚠️ [SoundService] Erro HTML Audio:", err);
      return false;
    }
  }

  /**
   * Toca criando um novo elemento Audio
   * @param {string} soundUrl - URL do som a ser tocado
   */
  async playWithNewAudio(soundUrl) {
    try {
      const audio = new Audio(soundUrl);
      audio.volume = this.volume;
      
      await audio.play();
      
      // Limpar após tocar
      audio.addEventListener("ended", () => {
        audio.remove();
      });
      
      return true;
    } catch (err) {
      console.warn("⚠️ [SoundService] Erro novo Audio:", err);
      return false;
    }
  }

  /**
   * Força desbloqueio do áudio (chamar em resposta a clique do usuário)
   */
  async forceUnlock() {
    try {
      if (this.audioContext && this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }
      
      // Tocar som silencioso para desbloquear (padrão)
      if (this.htmlAudio) {
        const originalVolume = this.htmlAudio.volume;
        this.htmlAudio.volume = 0.01;
        await this.htmlAudio.play();
        this.htmlAudio.pause();
        this.htmlAudio.volume = originalVolume;
        this.htmlAudio.currentTime = 0;
      }
      
      // Tocar som silencioso para desbloquear (grupos)
      if (this.htmlAudioGroup) {
        const originalVolume = this.htmlAudioGroup.volume;
        this.htmlAudioGroup.volume = 0.01;
        await this.htmlAudioGroup.play();
        this.htmlAudioGroup.pause();
        this.htmlAudioGroup.volume = originalVolume;
        this.htmlAudioGroup.currentTime = 0;
      }
      
      // ⭐ Tocar som silencioso para desbloquear (favoritos)
      if (this.htmlAudioStarred) {
        const originalVolume = this.htmlAudioStarred.volume;
        this.htmlAudioStarred.volume = 0.01;
        await this.htmlAudioStarred.play();
        this.htmlAudioStarred.pause();
        this.htmlAudioStarred.volume = originalVolume;
        this.htmlAudioStarred.currentTime = 0;
      }
      
      this.isUnlocked = true;
      console.log("🔓 [SoundService] Forçado desbloqueio de áudio");
      return true;
    } catch (err) {
      console.warn("⚠️ [SoundService] Erro ao forçar desbloqueio:", err);
      return false;
    }
  }

  /**
   * Verifica se o áudio está desbloqueado
   */
  isAudioUnlocked() {
    return this.isUnlocked;
  }

  /**
   * Retorna o estado do AudioContext
   */
  getAudioContextState() {
    return this.audioContext?.state || "unavailable";
  }
}

// Singleton - uma única instância para toda a aplicação
const soundService = new SoundService();

export default soundService;
